import { useParams, Link, useLocation } from "wouter";
import { useGetJob, getGetJobQueryKey, useDeleteJob, useRegenerateJob, useUpdateWordCuts, getListJobsQueryKey, getGetJobStatsQueryKey, useGetSocialPostByJob, getGetSocialPostByJobQueryKey, renderQuoteVideoApi, getQuoteVideoStatus, getQuoteVideoDownloadLink, ApiError } from "@workspace/api-client-react";
import type { QuoteVideo } from "@workspace/api-client-react";
import { useState, useRef, useEffect, useCallback } from "react";
import { useAudioUrlRefresh } from "@/hooks/useAudioUrlRefresh";
import { reportPlaybackError } from "@/lib/playbackTelemetry";
import { useAuth } from "@clerk/react";
import { trackEvent } from "@/lib/analytics";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import {
  Play, Pause, Download, RotateCcw, Activity,
  CheckCircle2, FileTerminal, Scissors, HardDrive, Clock,
  Quote, Zap, LayoutList, SkipForward, User,
  FileText, Copy, Check, ChevronDown, ChevronUp, Trash2, RefreshCw,
  AlertCircle, Loader2, Image, Film,
} from "lucide-react";
import { CreateVideoSection } from "@/components/CreateVideoSection";

function formatTime(t: number): string {
  if (isNaN(t) || !isFinite(t)) return "00:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function modeLabel(mode: string): string {
  if (mode === "key_quotes") return "Key Quotes";
  if (mode === "quick_catchup") return "Quick Catch-Up";
  return "Full Recap";
}

function modeIcon(mode: string) {
  if (mode === "key_quotes") return <Quote size={14} />;
  if (mode === "quick_catchup") return <Zap size={14} />;
  return <LayoutList size={14} />;
}

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

type WordItem = { text: string; start: number; end: number };
type WordCutRange = [number, number];

/** Convert an inclusive cut-range list into a per-word boolean flags array. */
function rangesToFlags(ranges: WordCutRange[] | undefined, n: number): boolean[] {
  const flags = new Array<boolean>(n).fill(false);
  if (!ranges) return flags;
  for (const [s, e] of ranges) {
    for (let i = Math.max(0, s); i <= Math.min(n - 1, e); i++) flags[i] = true;
  }
  return flags;
}

/** Compress a per-word boolean flags array into inclusive cut-range pairs. */
function flagsToRanges(flags: boolean[]): WordCutRange[] {
  const out: WordCutRange[] = [];
  let i = 0;
  while (i < flags.length) {
    if (!flags[i]) { i++; continue; }
    const start = i;
    while (i < flags.length && flags[i]) i++;
    out.push([start, i - 1]);
  }
  return out;
}

/** Total seconds removed by the supplied cut ranges, computed from word timestamps. */
function secondsCutFromRanges(words: WordItem[] | undefined, ranges: WordCutRange[] | undefined): number {
  if (!words || !ranges) return 0;
  let ms = 0;
  for (const [s, e] of ranges) {
    const a = words[s], b = words[e];
    if (a && b) ms += Math.max(0, b.end - a.start);
  }
  return ms / 1000;
}

/**
 * Render a sentence as either plain text (no cuts/words) or as a sequence of
 * clickable word spans with strikethrough on cut indices. Click toggles the
 * word's pending cut state via `onToggleWord`. Server-side validation is the
 * source of truth — clients may propose any combination, the server enforces
 * the WORD_CUT_* keep-guards and silently drops violations.
 */
function SegmentText({
  text,
  words,
  cutFlags,
  onToggleWord,
  isEditable,
  isDirty,
}: {
  text: string;
  words?: WordItem[];
  cutFlags: boolean[];
  onToggleWord?: (idx: number) => void;
  isEditable: boolean;
  isDirty: boolean;
}) {
  if (!words || words.length === 0) {
    // No per-word data → fall back to plain text. We cannot edit cuts here.
    return <span>{text}</span>;
  }

  return (
    <span>
      {words.map((w, i) => {
        const isCut = cutFlags[i] === true;
        const interactive = isEditable && !!onToggleWord;
        const className = [
          isCut ? "line-through text-muted-foreground/60 decoration-destructive/70" : "",
          interactive ? "cursor-pointer hover:bg-secondary/40 rounded-sm" : "",
          isDirty && isCut ? "bg-destructive/10" : "",
        ].filter(Boolean).join(" ");
        return (
          <span key={i}>
            <span
              className={className}
              onClick={interactive ? (e) => { e.stopPropagation(); onToggleWord!(i); } : undefined}
              data-testid={`word-${i}`}
              data-cut={isCut ? "true" : "false"}
              title={interactive ? (isCut ? "Click to keep" : "Click to cut") : undefined}
            >
              {w.text}
            </span>
            {i < words.length - 1 ? " " : ""}
          </span>
        );
      })}
    </span>
  );
}

const SOCIAL_POST_TERMINAL_STATUSES = new Set([
  "pending_approval",
  "approved",
  "discarded",
  "failed",
  "published",
]);

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ResultPage() {
  const params = useParams();
  const [, setLocation] = useLocation();
  const jobId = parseInt(params.id || "0", 10);
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: job, isLoading, isError } = useGetJob(jobId, {
    query: {
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        return status === "ready" || status === "failed" ? false : 2000;
      },
      queryKey: getGetJobQueryKey(jobId),
    },
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [downloadingQuote, setDownloadingQuote] = useState<number | null>(null);

  // ── Per-quote video state ──────────────────────────────────────────────────
  const [quoteVideos, setQuoteVideos] = useState<Map<number, QuoteVideo>>(new Map());
  const [renderingQuoteVideos, setRenderingQuoteVideos] = useState<Set<number>>(new Set());
  const [expandedQuoteVideo, setExpandedQuoteVideo] = useState<Set<number>>(new Set());
  const [quoteVideoFormats, setQuoteVideoFormats] = useState<Record<number, "portrait" | "landscape" | "square">>({});

  // Seed quote video records delivered by the job polling response so that
  // auto-generated videos (RSS + autoQuoteVideos) appear without user action.
  useEffect(() => {
    const incoming = job?.quoteVideos;
    if (!incoming?.length) return;
    setQuoteVideos((prev) => {
      const next = new Map(prev);
      for (const qv of incoming) {
        const existing = next.get(qv.quoteIndex);
        // Only overwrite when the incoming record is newer or there is none yet
        if (!existing || new Date(qv.updatedAt) > new Date(existing.updatedAt)) {
          next.set(qv.quoteIndex, qv);
        }
      }
      return next;
    });
  }, [job?.quoteVideos]);

  const audioRef = useRef<HTMLAudioElement>(null);
  const pollingQuoteIndicesRef = useRef<Set<number>>(new Set());
  const inflightRenderRequestsRef = useRef<Set<number>>(new Set());
  const { audioUrl, setAudioUrl, audioExpired, setAudioExpired, pendingRestoreRef, lastRefreshAtRef, expiresAtRef } = useAudioUrlRefresh({
    jobId,
    jobStatus: job?.status,
    jobOutputUrl: job?.outputUrl,
    getToken,
    audioRef,
  });

  const { mutate: deleteJob, isPending: isDeleting } = useDeleteJob({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListJobsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetJobStatsQueryKey() });
        setLocation("/jobs");
      },
      onError: () => {
        setShowDeleteConfirm(false);
        toast({ title: "Delete failed", description: "Could not delete this job. Please try again.", variant: "destructive" });
      },
    },
  });

  const [showRegenPanel, setShowRegenPanel] = useState(false);
  const [regenMode, setRegenMode] = useState<string | null>(null);
  const [regenTarget, setRegenTarget] = useState<number | null>(null);

  const { mutate: regenerateJob, isPending: isRegenerating } = useRegenerateJob({
    mutation: {
      onSuccess: () => {
        setAudioExpired(false);
        queryClient.invalidateQueries({ queryKey: getGetJobQueryKey(jobId) });
      },
      onError: (err: unknown) => {
        const msg = (err as { message?: string })?.message ?? "Could not regenerate this job. Please try again.";
        toast({ title: "Regenerate failed", description: msg, variant: "destructive" });
        setShowRegenPanel(false);
      },
    },
  });

  const handleRegenerate = () => {
    const mode = (regenMode ?? job?.mode) as string;
    const targetPercent = regenTarget ?? job?.targetPercent ?? 40;
    regenerateJob({ id: jobId, data: { mode: mode as "key_quotes" | "quick_catchup" | "full_recap", targetPercent } });
  };

  // ── Word-cut editor state ─────────────────────────────────────────────────
  // Per-segment override of `wordCuts` while the user is editing. Each entry
  // is the full per-word boolean array for that segment; absence = unchanged.
  const [editedFlags, setEditedFlags] = useState<Map<number, boolean[]>>(new Map());
  const [isEditingCuts, setIsEditingCuts] = useState(false);

  const { mutate: updateWordCuts, isPending: isSavingCuts } = useUpdateWordCuts({
    mutation: {
      onSuccess: () => {
        setEditedFlags(new Map());
        setIsEditingCuts(false);
        queryClient.invalidateQueries({ queryKey: getGetJobQueryKey(jobId) });
        toast({ title: "Cuts updated", description: "Audio re-stitched with your changes." });
      },
      onError: (err: unknown) => {
        const msg = (err as { message?: string })?.message ?? "Could not save your edits. Please try again.";
        toast({ title: "Save failed", description: msg, variant: "destructive" });
      },
    },
  });

  const toggleWordCut = useCallback((segmentId: number, wordIdx: number, totalWords: number, currentCuts: WordCutRange[] | undefined) => {
    setEditedFlags((prev) => {
      const next = new Map(prev);
      const existing = next.get(segmentId) ?? rangesToFlags(currentCuts, totalWords);
      const updated = existing.slice();
      updated[wordIdx] = !updated[wordIdx];
      next.set(segmentId, updated);
      return next;
    });
  }, []);

  const handleSaveCuts = useCallback(() => {
    if (!job?.keyQuoteSegments) return;
    const payload: { segment_id: number; drop_word_ranges: WordCutRange[] }[] = [];
    for (const seg of job.keyQuoteSegments) {
      const flags = editedFlags.get(seg.segment_id);
      if (flags) {
        payload.push({ segment_id: seg.segment_id, drop_word_ranges: flagsToRanges(flags) });
      } else if (seg.wordCuts && seg.wordCuts.length > 0) {
        // Preserve existing cuts for untouched segments.
        payload.push({ segment_id: seg.segment_id, drop_word_ranges: seg.wordCuts as WordCutRange[] });
      }
    }
    updateWordCuts({ id: jobId, data: { wordCuts: payload } });
  }, [job, editedFlags, updateWordCuts, jobId]);

  const handleCancelCuts = useCallback(() => {
    setEditedFlags(new Map());
    setIsEditingCuts(false);
  }, []);

  const { data: socialPostData } = useGetSocialPostByJob(jobId, {
    query: {
      retry: false,
      enabled: !!jobId,
      queryKey: getGetSocialPostByJobQueryKey(jobId),
      refetchInterval: (query) => {
        const status = query.state.data?.post?.status;
        if (!status || SOCIAL_POST_TERMINAL_STATUSES.has(status)) return false;
        return 3000;
      },
    },
  });
  const socialPost = socialPostData?.post ?? null;

  const [thumbnailBlobUrl, setThumbnailBlobUrl] = useState<string | null>(null);
  const [isDownloadingThumbnail, setIsDownloadingThumbnail] = useState(false);
  const [isDownloadingVideo, setIsDownloadingVideo] = useState(false);
  const [videoDownloadProgress, setVideoDownloadProgress] = useState<number | null>(null);
  const [videoDownloadedBytes, setVideoDownloadedBytes] = useState<number | null>(null);

  useEffect(() => {
    if (!socialPost?.thumbnailUrl || !socialPost?.id) return;
    let revoked = false;
    (async () => {
      const token = await getToken();
      const res = await fetch(`${basePath}/api/social/posts/${socialPost.id}/thumbnail`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok || revoked) return;
      const blob = await res.blob();
      if (revoked) return;
      const url = URL.createObjectURL(blob);
      setThumbnailBlobUrl(url);
    })();
    return () => {
      revoked = true;
      setThumbnailBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [socialPost?.id, socialPost?.thumbnailUrl, getToken]);

  const handleDownloadThumbnail = useCallback(async () => {
    if (isDownloadingThumbnail || !socialPost?.id || !socialPost?.thumbnailUrl) return;
    setIsDownloadingThumbnail(true);
    try {
      const token = await getToken();
      const res = await fetch(`${basePath}/api/social/posts/${socialPost.id}/thumbnail`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `social_thumbnail_job_${jobId}.jpg`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      toast({ title: "Download failed", description: "Could not download the thumbnail.", variant: "destructive" });
    } finally {
      setIsDownloadingThumbnail(false);
    }
  }, [isDownloadingThumbnail, socialPost?.id, socialPost?.thumbnailUrl, getToken, jobId, toast]);

  const handleDownloadVideo = useCallback(async () => {
    if (isDownloadingVideo || !socialPost?.id || !socialPost?.videoUrl) return;
    setIsDownloadingVideo(true);
    setVideoDownloadProgress(null);
    setVideoDownloadedBytes(null);
    try {
      const token = await getToken();
      const res = await fetch(`${basePath}/api/social/posts/${socialPost.id}/video-url`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const { url } = await res.json() as { url: string };
      const videoRes = await fetch(url);
      if (!videoRes.ok) throw new Error("Download failed");
      const contentLength = videoRes.headers.get("Content-Length");
      const total = contentLength ? parseInt(contentLength, 10) : null;
      const reader = videoRes.body?.getReader();
      let blob: Blob;
      if (reader) {
        const chunks: BlobPart[] = [];
        let received = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value as BlobPart);
          received += value.byteLength;
          if (total) {
            setVideoDownloadProgress(Math.min(100, Math.round((received / total) * 100)));
          } else {
            setVideoDownloadedBytes(received);
          }
        }
        blob = new Blob(chunks);
      } else {
        blob = await videoRes.blob();
      }
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `social_video_job_${jobId}.mp4`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
    } catch {
      toast({ title: "Download failed", description: "Could not download the video.", variant: "destructive" });
    } finally {
      setIsDownloadingVideo(false);
      setVideoDownloadProgress(null);
      setVideoDownloadedBytes(null);
    }
  }, [isDownloadingVideo, socialPost?.id, socialPost?.videoUrl, getToken, jobId, toast]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeQuote, setActiveQuote] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleDownload = async () => {
    if (isDownloading || !job?.outputUrl) return;
    setIsDownloading(true);
    try {
      const token = await getToken();
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
      const urlRes = await fetch(`${basePath}/api/jobs/${jobId}/audio-url`, { headers });
      if (urlRes.status === 410) {
        setAudioExpired(true);
        return;
      }
      let fetchUrl: string;
      let fetchInit: RequestInit = {};
      if (urlRes.ok) {
        const { url: signedUrl } = await urlRes.json() as { url: string; expiresAt: string };
        fetchUrl = signedUrl;
      } else if (urlRes.status === 404) {
        // Legacy disk-based job — fall back to proxied download route
        fetchUrl = `${basePath}/api/jobs/${jobId}/download`;
        fetchInit = { headers };
      } else {
        throw new Error("Download failed");
      }
      const audioRes = await fetch(fetchUrl, fetchInit);
      if (!audioRes.ok) throw new Error("Download failed");
      const blob = await audioRes.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `abbreviated_job_${jobId}.mp3`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      toast({ title: "Download failed", description: "Could not download the audio file. Please try again.", variant: "destructive" });
    } finally {
      setIsDownloading(false);
    }
  };

  // Shared poll loop — guarded by pollingQuoteIndicesRef to prevent duplicate polls
  // from both user-triggered renders and auto-generated videos seeded from job data.
  const startPollForQuote = useCallback((index: number) => {
    if (pollingQuoteIndicesRef.current.has(index)) return;
    pollingQuoteIndicesRef.current.add(index);
    setRenderingQuoteVideos((prev) => new Set(prev).add(index));
    const poll = async () => {
      try {
        const status = await getQuoteVideoStatus(jobId, index);
        setQuoteVideos((prev) => new Map(prev).set(index, status));
        if (status.videoStatus === "rendering") {
          setTimeout(() => void poll(), 3000);
        } else {
          pollingQuoteIndicesRef.current.delete(index);
          setRenderingQuoteVideos((prev) => { const next = new Set(prev); next.delete(index); return next; });
          if (status.videoStatus === "failed") {
            // Surface a real render failure so the user isn't left guessing
            // when the spinner disappears. Inline retry is rendered below the
            // quote (see Film-button block) and re-runs the previously
            // selected format.
            toast({
              title: "Video render failed",
              description: status.videoErrorMessage ?? "Something went wrong rendering this quote video. Please try again.",
              variant: "destructive",
            });
          }
        }
      } catch {
        pollingQuoteIndicesRef.current.delete(index);
        setRenderingQuoteVideos((prev) => { const next = new Set(prev); next.delete(index); return next; });
      }
    };
    setTimeout(() => void poll(), 3000);
  }, [jobId, toast]);

  const handleRenderQuoteVideo = useCallback(async (index: number, format: "portrait" | "landscape" | "square" = "portrait") => {
    // Synchronous guard against rapid re-clicks: React state updates lag behind
    // the click event, so we use a ref Set that is mutated immediately. This
    // prevents a duplicate POST that would otherwise return 409 from the server.
    if (
      inflightRenderRequestsRef.current.has(index) ||
      renderingQuoteVideos.has(index) ||
      pollingQuoteIndicesRef.current.has(index)
    ) {
      return;
    }
    inflightRenderRequestsRef.current.add(index);
    setRenderingQuoteVideos((prev) => new Set(prev).add(index));
    setExpandedQuoteVideo((prev) => { const next = new Set(prev); next.delete(index); return next; });
    try {
      const record = await renderQuoteVideoApi(jobId, index, { format, imageSource: "artwork" });
      setQuoteVideos((prev) => new Map(prev).set(index, record));
      if (record.videoStatus === "rendering") {
        startPollForQuote(index);
      } else {
        // Already done (server returned a cached/existing render)
        setRenderingQuoteVideos((prev) => { const next = new Set(prev); next.delete(index); return next; });
      }
    } catch (err) {
      // 409 = "Render already in progress for this quote". This is benign:
      // a previous request (often a rapid double-click) already kicked off the
      // render. Treat it as success — fetch the current status and start
      // polling so the spinner stays visible until the render completes.
      if (err instanceof ApiError && (err as ApiError).status === 409) {
        try {
          const status = await getQuoteVideoStatus(jobId, index);
          setQuoteVideos((prev) => new Map(prev).set(index, status));
          if (status.videoStatus === "rendering") {
            startPollForQuote(index);
          } else {
            setRenderingQuoteVideos((prev) => { const next = new Set(prev); next.delete(index); return next; });
          }
        } catch {
          // Status fetch failed, but the original render is presumably still in
          // progress on the server. Start polling anyway so the user sees the
          // result when the worker finishes.
          startPollForQuote(index);
        }
      } else {
        toast({ title: "Render failed", description: "Could not start video render for this quote.", variant: "destructive" });
        setRenderingQuoteVideos((prev) => { const next = new Set(prev); next.delete(index); return next; });
      }
    } finally {
      inflightRenderRequestsRef.current.delete(index);
    }
  }, [renderingQuoteVideos, jobId, toast, startPollForQuote]);

  // Auto-start polling for any quote videos that arrive in "rendering" state
  // (e.g. auto-generated videos from autoQuoteVideos RSS setting).
  useEffect(() => {
    quoteVideos.forEach((qv, idx) => {
      if (qv.videoStatus === "rendering") {
        startPollForQuote(idx);
      }
    });
  }, [quoteVideos, startPollForQuote]);

  const handleDownloadQuoteVideo = useCallback(async (index: number) => {
    try {
      // Get a short-lived signed URL from the server, then download directly from GCS.
      // This avoids buffering the entire MP4 through the API proxy.
      const { url } = await getQuoteVideoDownloadLink(jobId, index);
      const a = document.createElement("a");
      a.href = url;
      a.download = `quote_${index + 1}_video_job_${jobId}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      toast({ title: "Download failed", description: "Could not download the quote video.", variant: "destructive" });
    }
  }, [jobId, toast]);

  const handleDownloadQuote = useCallback(async (index: number, originalFilename?: string | null) => {
    if (downloadingQuote !== null) return;
    setDownloadingQuote(index);
    try {
      const token = await getToken();
      const res = await fetch(`${basePath}/api/jobs/${jobId}/quotes/${index}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeBase = (originalFilename ?? `job_${jobId}`).replace(/\.[^.]+$/, "");
      a.download = `quote_${index + 1}_${safeBase}.mp3`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      toast({ title: "Download failed", description: "Could not download this clip.", variant: "destructive" });
    } finally {
      setDownloadingQuote(null);
    }
  }, [downloadingQuote, getToken, jobId, toast]);

  useEffect(() => {
    if (audioUrl && audioRef.current) {
      const audio = audioRef.current;
      const onMeta = () => {
        setDuration(audio.duration);
        // If a URL refresh just happened, restore the saved playback position
        // and resume if the user was playing before the refresh.
        const restore = pendingRestoreRef.current;
        if (restore) {
          pendingRestoreRef.current = null;
          audio.currentTime = restore.time;
          if (restore.wasPlaying) {
            audio.play().catch(() => {});
          }
        }
      };
      const onTime = () => {
        setCurrentTime(audio.currentTime);
        // Track active quote for Key Quotes mode
        if (job?.mode === "key_quotes" && job.keyQuoteSegments) {
          const ct = audio.currentTime;
          let found = -1;
          for (let i = 0; i < job.keyQuoteSegments.length; i++) {
            const seg = job.keyQuoteSegments[i];
            const start = seg.outputStartSeconds ?? 0;
            const nextSeg = job.keyQuoteSegments[i + 1];
            const end = nextSeg?.outputStartSeconds ?? audio.duration;
            if (ct >= start && ct < end) {
              found = i;
              break;
            }
          }
          setActiveQuote(found >= 0 ? found : null);
        }
      };
      const onEnd = () => { setIsPlaying(false); setIsBuffering(false); };
      const onPause = () => { setIsPlaying(false); setIsBuffering(false); };
      const onPlaying = () => setIsBuffering(false);
      const onWaiting = () => setIsBuffering(true);
      const onError = () => {
        setIsPlaying(false);
        setIsBuffering(false);
        // Capture structured diagnostics + send to telemetry endpoint so we
        // can root-cause future playback failures from server logs instead
        // of guessing from screenshots. Best-effort, never throws.
        void reportPlaybackError({
          audio,
          jobId,
          page: "result",
          eventType: "error",
          lastRefreshAt: lastRefreshAtRef.current,
          expiresAt: expiresAtRef.current,
          getToken,
        });
        // The MediaError code is the most actionable signal we have for the
        // user — code 2 (network) and 4 (src not supported) usually mean the
        // stream itself was blocked (commonly: GCS CORS missing on Range
        // responses, or a corporate proxy stripping the body); code 3
        // (decode) means the bytes arrived but couldn't be decoded.
        const code = audio.error?.code;
        const description =
          code === 2
            ? "Your network blocked the audio stream. Try refreshing the page."
            : code === 3
            ? "The audio file couldn't be decoded. Try downloading it instead."
            : code === 4
            ? "The audio stream couldn't be loaded. Try refreshing the page."
            : "Something went wrong during playback. Please try again.";
        toast({ title: "Playback error", description, variant: "destructive" });
      };

      let recoveryTimer: ReturnType<typeof setTimeout> | null = null;
      const attemptRecovery = () => {
        if (recoveryTimer) return;
        // Surface stalls/waits as telemetry too so we can tell apart slow
        // network from hard CORS rejections in the logs.
        void reportPlaybackError({
          audio,
          jobId,
          page: "result",
          eventType: "stalled",
          lastRefreshAt: lastRefreshAtRef.current,
          expiresAt: expiresAtRef.current,
          getToken,
        });
        recoveryTimer = setTimeout(() => {
          recoveryTimer = null;
          if (!audio.paused && !audio.ended) {
            const pos = audio.currentTime;
            audio.currentTime = pos + 0.001;
            audio.play().catch(() => {
              setIsPlaying(false);
              setIsBuffering(false);
            });
          }
        }, 300);
      };

      audio.addEventListener("loadedmetadata", onMeta);
      audio.addEventListener("timeupdate", onTime);
      audio.addEventListener("ended", onEnd);
      audio.addEventListener("pause", onPause);
      audio.addEventListener("playing", onPlaying);
      audio.addEventListener("stalled", attemptRecovery);
      audio.addEventListener("waiting", onWaiting);
      audio.addEventListener("waiting", attemptRecovery);
      audio.addEventListener("error", onError);
      return () => {
        if (recoveryTimer) clearTimeout(recoveryTimer);
        audio.removeEventListener("loadedmetadata", onMeta);
        audio.removeEventListener("timeupdate", onTime);
        audio.removeEventListener("ended", onEnd);
        audio.removeEventListener("pause", onPause);
        audio.removeEventListener("playing", onPlaying);
        audio.removeEventListener("stalled", attemptRecovery);
        audio.removeEventListener("waiting", onWaiting);
        audio.removeEventListener("waiting", attemptRecovery);
        audio.removeEventListener("error", onError);
      };
    }
    return undefined;
  }, [audioUrl, job?.mode, job?.keyQuoteSegments]);

  useEffect(() => {
    if (job && job.status !== "ready" && job.status !== "failed") {
      setLocation(`/jobs/${jobId}`);
    }
  }, [job?.status, jobId, setLocation]);

  const trackedStatusRef = useRef<string | null>(null);
  useEffect(() => {
    if (!job) return;
    if (job.status === "ready" && trackedStatusRef.current !== "ready") {
      trackedStatusRef.current = "ready";
      trackEvent("job_completed", { mode: job.mode, source: job.sourceType ?? "upload", job_id: jobId });
    } else if (job.status === "failed" && trackedStatusRef.current !== "failed") {
      trackedStatusRef.current = "failed";
      trackEvent("job_failed", { mode: job.mode, source: job.sourceType ?? "upload", job_id: jobId });
    }
  }, [job?.status, job?.mode, job?.sourceType, jobId]);

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        setIsPlaying(false);
      });
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = Number(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const seekToQuote = useCallback((outputStartSeconds: number, index: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = outputStartSeconds;
    setCurrentTime(outputStartSeconds);
    setActiveQuote(index);
    void audioRef.current.play();
    setIsPlaying(true);
  }, []);

  const copyTranscript = useCallback(async (segments: typeof quotes) => {
    const text = segments.map((s) => `[${s.speaker}] ${s.text}`).join("\n\n");
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin text-primary">
          <Activity size={32} />
        </div>
      </div>
    );
  }

  if (isError || !job) {
    return (
      <div className="max-w-xl mx-auto mt-12 text-center space-y-4">
        <CheckCircle2 className="mx-auto h-12 w-12 text-destructive" />
        <h2 className="text-xl font-bold">Job Not Found</h2>
        <Button asChild variant="outline">
          <Link href="/">New Job</Link>
        </Button>
      </div>
    );
  }

  if (job.status === "failed") {
    return (
      <div className="max-w-xl mx-auto mt-12 text-center space-y-4">
        <h2 className="text-xl font-bold text-destructive">Pipeline Failed</h2>
        <p className="text-muted-foreground font-mono text-sm bg-destructive/5 p-4 rounded-md border border-destructive/20">
          {job.errorMessage || "Unknown processing error"}
        </p>
        <Button asChild variant="outline">
          <Link href="/"><RotateCcw className="mr-2 h-4 w-4" /> Try Again</Link>
        </Button>
      </div>
    );
  }

  const compressionPct =
    job.originalDurationSeconds && job.outputDurationSeconds
      ? Math.round((1 - job.outputDurationSeconds / job.originalDurationSeconds) * 100)
      : null;

  const isKeyQuotes = job.mode === "key_quotes";
  const isFullRecap = job.mode === "full_recap";
  const quotes = job.keyQuoteSegments ?? [];

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-in fade-in duration-500">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-3xl font-mono font-bold tracking-tight">Result</h1>
            <Badge variant="outline" className="font-mono text-xs flex items-center gap-1">
              {modeIcon(job.mode)} {modeLabel(job.mode)}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground font-mono truncate max-w-lg" title={job.originalFilename}>
            {job.originalFilename}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {job.outputUrl && (
            <Button
              className="font-mono uppercase text-xs tracking-wider"
              data-testid="button-download"
              onClick={handleDownload}
              disabled={isDownloading}
            >
              <Download className="mr-2 h-4 w-4" /> {isDownloading ? "Downloading…" : "Download MP3"}
            </Button>
          )}
          {job.sourceType === "rss" && !showDeleteConfirm && (
            <Button
              variant="outline"
              className="font-mono uppercase text-xs tracking-wider"
              onClick={() => {
                setRegenMode(job.mode);
                setRegenTarget(job.targetPercent ?? 40);
                setShowRegenPanel((v) => !v);
              }}
              disabled={isRegenerating}
              data-testid="button-regenerate"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Regenerate
            </Button>
          )}
          {showDeleteConfirm ? (
            <div className="flex gap-2 items-center">
              <span className="text-xs font-mono text-muted-foreground">Delete this job?</span>
              <Button
                variant="destructive"
                className="font-mono uppercase text-xs tracking-wider"
                onClick={() => deleteJob({ id: jobId })}
                disabled={isDeleting}
                data-testid="button-delete-confirm"
              >
                {isDeleting ? "Deleting…" : "Confirm"}
              </Button>
              <Button
                variant="outline"
                className="font-mono uppercase text-xs tracking-wider"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                data-testid="button-delete-cancel"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              className="font-mono uppercase text-xs tracking-wider text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="button-delete"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
          )}
        </div>
      </div>

      {/* Audio Expired Panel */}
      {audioExpired && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3" data-testid="panel-audio-expired">
          <AlertCircle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-mono text-amber-200 leading-snug">
              Audio has expired — regenerate this job to download it.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => regenerateJob({ id: jobId, data: { mode: (job?.mode ?? "full_recap") as "key_quotes" | "quick_catchup" | "full_recap", targetPercent: job?.targetPercent ?? 40 } })}
              disabled={isRegenerating}
              className="mt-2 font-mono text-xs gap-1.5 border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
              data-testid="button-regenerate-expired"
            >
              {isRegenerating ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <RefreshCw size={12} />
              )}
              {isRegenerating ? "Regenerating…" : "Regenerate Job"}
            </Button>
          </div>
        </div>
      )}

      {/* Regenerate Panel */}
      {showRegenPanel && (
        <Card className="bg-card/50 border-border/50 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="card-regenerate">
          <CardContent className="pt-5 pb-5 space-y-4">
            <div className="space-y-1">
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Abbreviation Mode</p>
              <div className="flex gap-2 flex-wrap">
                {(["key_quotes", "quick_catchup", "full_recap"] as const).map((m) => (
                  <Button
                    key={m}
                    size="sm"
                    variant={regenMode === m ? "default" : "outline"}
                    className="font-mono text-xs gap-1"
                    onClick={() => setRegenMode(m)}
                    disabled={isRegenerating}
                  >
                    {m === "key_quotes" && <Quote size={12} />}
                    {m === "quick_catchup" && <Zap size={12} />}
                    {m === "full_recap" && <LayoutList size={12} />}
                    {modeLabel(m)}
                  </Button>
                ))}
              </div>
            </div>

            {regenMode === "full_recap" && (
              <div className="space-y-1">
                <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Target Length</p>
                <div className="flex gap-2 flex-wrap">
                  {[20, 30, 40, 50, 60].map((pct) => (
                    <Button
                      key={pct}
                      size="sm"
                      variant={regenTarget === pct ? "default" : "outline"}
                      className="font-mono text-xs"
                      onClick={() => setRegenTarget(pct)}
                      disabled={isRegenerating}
                    >
                      {pct}%
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground font-mono">
              This will re-download and re-process the episode. It counts as one abbreviation against your monthly quota.
            </p>

            <div className="flex gap-2">
              <Button
                size="sm"
                className="font-mono uppercase text-xs tracking-wider"
                onClick={handleRegenerate}
                disabled={isRegenerating}
                data-testid="button-regenerate-confirm"
              >
                <RefreshCw className="mr-2 h-3 w-3" />
                {isRegenerating ? "Starting…" : "Regenerate"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="font-mono uppercase text-xs tracking-wider"
                onClick={() => setShowRegenPanel(false)}
                disabled={isRegenerating}
                data-testid="button-regenerate-cancel"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Audio Player */}
      {job.outputUrl && (
        <Card className="bg-card/50 border-border/50" data-testid="card-audio-player">
          <CardHeader className="pb-4 border-b border-border/10">
            <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
              <CheckCircle2 size={16} className="text-primary" />
              {isKeyQuotes ? "Combined Highlights Audio" : "Abbreviated Audio"}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {/*
              crossOrigin="anonymous" forces the browser to perform a real CORS
              fetch instead of a silent "no-cors" media load. With CORS the
              browser surfaces a structured MediaError (code 4 / SRC_NOT_SUPPORTED
              when the bucket lacks GET/Range CORS rules) which our telemetry
              hook captures. Without it, missing CORS just silently stalls and
              we lose all signal — see task #914.
            */}
            {audioUrl && <audio ref={audioRef} src={audioUrl} preload="metadata" crossOrigin="anonymous" className="hidden" />}

            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="icon"
                className="h-12 w-12 rounded-full border-primary/50 text-primary flex-shrink-0"
                onClick={togglePlayPause}
                data-testid="button-play-pause"
              >
                {isBuffering
                  ? <Loader2 size={18} className="animate-spin" />
                  : isPlaying
                    ? <Pause size={18} />
                    : <Play size={18} />}
              </Button>

              <div className="flex-1 space-y-1">
                <input
                  type="range"
                  min={0}
                  max={duration || 100}
                  step={0.1}
                  value={currentTime}
                  onChange={handleSeek}
                  className="w-full h-1.5 accent-primary cursor-pointer"
                  data-testid="input-seek-bar"
                />
                <div className="flex justify-between text-xs font-mono text-muted-foreground">
                  <span data-testid="text-current-time">{formatTime(currentTime)}</span>
                  <span data-testid="text-total-time">{formatTime(duration)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Video */}
      {job.outputUrl && (
        <CreateVideoSection job={job} jobId={jobId} />
      )}

      {/* Social Post Preview */}
      {socialPost && (socialPost.thumbnailUrl || socialPost.videoUrl) && (
        <Card className="bg-card/50 border-border/50" data-testid="card-social-post">
          <CardHeader className="pb-4 border-b border-border/10">
            <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
              <Film size={16} className="text-primary" />
              Social Post
              {(() => {
                const s = socialPost.status;
                const label =
                  s === "pending_approval" ? "Pending Approval" :
                  s === "approved" ? "Approved" :
                  s === "discarded" ? "Discarded" :
                  s === "failed" ? "Failed" :
                  s === "partial" ? "Partially Published" :
                  s === "published" ? "Published" :
                  s === "generating" ? "Generating" : s;
                const variant =
                  s === "approved" || s === "published" ? "default" :
                  s === "discarded" || s === "failed" ? "destructive" :
                  "secondary";
                return (
                  <Badge variant={variant} className="font-mono text-xs ml-auto" data-testid="badge-social-post-status">
                    {label}
                  </Badge>
                );
              })()}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5 space-y-5">
            {socialPost.thumbnailUrl && (
              <div className="space-y-2" data-testid="section-social-thumbnail">
                <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Image size={12} /> Thumbnail
                </p>
                <div className="flex items-start gap-4">
                  {thumbnailBlobUrl ? (
                    <img
                      src={thumbnailBlobUrl}
                      alt="Social post thumbnail"
                      className="rounded-md border border-border/40 object-cover w-40 h-[90px] flex-shrink-0"
                      data-testid="img-social-thumbnail"
                    />
                  ) : (
                    <div className="w-40 h-[90px] rounded-md border border-border/40 bg-secondary/30 flex items-center justify-center flex-shrink-0">
                      <Loader2 size={16} className="animate-spin text-muted-foreground" />
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="font-mono text-xs gap-1.5"
                    onClick={handleDownloadThumbnail}
                    disabled={isDownloadingThumbnail}
                    data-testid="button-download-thumbnail"
                  >
                    {isDownloadingThumbnail ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                    {isDownloadingThumbnail ? "Downloading…" : "Download Thumbnail"}
                  </Button>
                </div>
              </div>
            )}
            {socialPost.videoUrl && (
              <div className="space-y-2" data-testid="section-social-video">
                <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Film size={12} /> Video
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="font-mono text-xs gap-1.5"
                  onClick={handleDownloadVideo}
                  disabled={isDownloadingVideo}
                  data-testid="button-download-video"
                >
                  {isDownloadingVideo ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                  {isDownloadingVideo
                    ? videoDownloadProgress !== null
                      ? `Downloading… ${videoDownloadProgress}%`
                      : videoDownloadedBytes !== null
                        ? `Downloading… ${formatBytes(videoDownloadedBytes)}`
                        : "Downloading…"
                    : "Download Video"}
                </Button>
                {isDownloadingVideo && (
                  <Progress
                    value={videoDownloadProgress !== null ? videoDownloadProgress : undefined}
                    className={`h-1.5 w-full${videoDownloadProgress === null ? " animate-pulse" : ""}`}
                    data-testid="video-download-progress-bar"
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Key Quotes navigable list */}
      {isKeyQuotes && quotes.length > 0 && (
        <Card className="bg-card/50 border-border/50" data-testid="card-key-quotes">
          <CardHeader className="pb-4 border-b border-border/10">
            <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
              <Quote size={16} className="text-primary" />
              Key Quotes ({quotes.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            {quotes.map((quote, idx) => {
              const isActive = activeQuote === idx;
              const outputStart = quote.outputStartSeconds ?? 0;
              return (
                <div
                  key={quote.segment_id}
                  data-testid={`quote-item-${idx}`}
                  className={[
                    "rounded-lg border p-4 transition-all duration-200 cursor-pointer group",
                    isActive
                      ? "border-primary/60 bg-primary/10"
                      : "border-border/40 hover:border-primary/30 hover:bg-secondary/20",
                  ].join(" ")}
                  onClick={() => {
                    if (isActive && isPlaying) {
                      togglePlayPause();
                    } else {
                      seekToQuote(outputStart, idx);
                    }
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {quote.speaker && (
                        <div className="flex items-center gap-1.5 mb-2">
                          <User size={10} className="text-muted-foreground" />
                          <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                            {quote.speaker}
                          </span>
                        </div>
                      )}
                      <p className="text-sm leading-relaxed">{quote.text}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs font-mono text-muted-foreground">
                          {formatTime(quote.start / 1000)} – {formatTime(quote.end / 1000)}
                          <span className="ml-1 text-muted-foreground/50">source</span>
                        </span>
                        <span className="text-xs font-mono text-primary">
                          @ {formatTime(outputStart)} output
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className={[
                          "h-8 w-8 rounded-full transition-colors",
                          isActive ? "text-primary bg-primary/20" : "text-muted-foreground group-hover:text-primary",
                        ].join(" ")}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isActive && isPlaying) {
                            togglePlayPause();
                          } else {
                            seekToQuote(outputStart, idx);
                          }
                        }}
                        data-testid={`button-jump-to-quote-${idx}`}
                      >
                        {isActive && isPlaying ? <Pause size={14} /> : <SkipForward size={14} />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full transition-colors text-muted-foreground hover:text-primary"
                        title="Download this clip"
                        disabled={downloadingQuote !== null}
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDownloadQuote(idx, job?.originalFilename);
                        }}
                        data-testid={`button-download-quote-${idx}`}
                      >
                        <Download size={14} className={downloadingQuote === idx ? "animate-pulse" : ""} />
                      </Button>
                      {/* Per-quote video: icon button toggles format panel */}
                      {(() => {
                        const qv = quoteVideos.get(idx);
                        const isRendering = renderingQuoteVideos.has(idx) || qv?.videoStatus === "rendering";
                        const hasVideo = qv?.videoUrl != null && qv?.videoStatus == null;
                        const isPanelOpen = expandedQuoteVideo.has(idx);

                        if (hasVideo) {
                          return (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-full text-sky-400 hover:text-sky-300 transition-colors"
                              title="Download quote video"
                              data-testid={`button-quote-video-${idx}`}
                              onClick={(e) => { e.stopPropagation(); void handleDownloadQuoteVideo(idx); }}
                            >
                              <Download size={14} />
                            </Button>
                          );
                        }

                        return (
                          <Button
                            variant="ghost"
                            size="icon"
                            className={[
                              "h-8 w-8 rounded-full transition-colors",
                              isRendering
                                ? "text-sky-400"
                                : isPanelOpen
                                  ? "text-sky-400 bg-sky-500/10"
                                  : qv?.videoStatus === "failed"
                                    ? "text-destructive/60 hover:text-destructive"
                                    : "text-muted-foreground/50 hover:text-sky-400",
                            ].join(" ")}
                            title={
                              isRendering
                                ? "Rendering video…"
                                : isPanelOpen
                                  ? "Cancel"
                                  : qv?.videoStatus === "failed"
                                    ? "Retry video render"
                                    : "Create short video clip"
                            }
                            disabled={isRendering}
                            data-testid={`button-quote-video-${idx}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isRendering) return;
                              setExpandedQuoteVideo((prev) => {
                                const next = new Set(prev);
                                if (next.has(idx)) { next.delete(idx); } else { next.add(idx); }
                                return next;
                              });
                            }}
                          >
                            {isRendering ? <Loader2 size={14} className="animate-spin" /> : <Film size={14} />}
                          </Button>
                        );
                      })()}
                    </div>
                  </div>
                  {/* Inline format picker — shown when Film icon is clicked */}
                  {expandedQuoteVideo.has(idx) && !(renderingQuoteVideos.has(idx) || quoteVideos.get(idx)?.videoStatus === "rendering") && (
                    <div className="mt-3 pt-3 border-t border-border/30" onClick={(e) => e.stopPropagation()}>
                      <p className="text-xs text-muted-foreground mb-2 font-medium">Video format</p>
                      <div className="flex gap-2 mb-3">
                        {(
                          [
                            { value: "portrait" as const, label: "Portrait", ratio: "9:16" },
                            { value: "landscape" as const, label: "Landscape", ratio: "16:9" },
                            { value: "square" as const, label: "Square", ratio: "1:1" },
                          ] as const
                        ).map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => setQuoteVideoFormats((prev) => ({ ...prev, [idx]: opt.value }))}
                            className={`px-2.5 py-1 rounded-md border text-xs transition-colors ${
                              (quoteVideoFormats[idx] ?? "portrait") === opt.value
                                ? "border-sky-500 bg-sky-500/10 text-sky-400"
                                : "border-border text-muted-foreground hover:border-foreground/40"
                            }`}
                          >
                            {opt.label} <span className="opacity-60">{opt.ratio}</span>
                          </button>
                        ))}
                      </div>
                      <Button
                        size="sm"
                        className="h-7 text-xs bg-sky-600 hover:bg-sky-700 text-white"
                        onClick={() => void handleRenderQuoteVideo(idx, quoteVideoFormats[idx] ?? "portrait")}
                      >
                        <Film size={12} className="mr-1.5" />
                        Create Video
                      </Button>
                    </div>
                  )}
                  {/* Inline failure message + one-click retry */}
                  {quoteVideos.get(idx)?.videoStatus === "failed" && !renderingQuoteVideos.has(idx) && (() => {
                    const qv = quoteVideos.get(idx)!;
                    const retryFormat = qv.videoFormat ?? quoteVideoFormats[idx] ?? "portrait";
                    return (
                      <div
                        className="mt-3 pt-3 border-t border-border/30"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`quote-video-error-${idx}`}
                      >
                        <div className="flex items-start gap-2">
                          <AlertCircle size={14} className="shrink-0 mt-0.5 text-destructive" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-destructive">Video render failed</p>
                            <p className="text-xs text-muted-foreground mt-0.5 break-words">
                              {qv.videoErrorMessage ?? "Something went wrong rendering this quote video."}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs gap-1.5 shrink-0"
                            onClick={() => void handleRenderQuoteVideo(idx, retryFormat)}
                            data-testid={`button-retry-quote-video-${idx}`}
                          >
                            <RotateCcw size={12} />
                            Retry
                          </Button>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Progress bar — shown while rendering */}
                  {(renderingQuoteVideos.has(idx) || quoteVideos.get(idx)?.videoStatus === "rendering") && (() => {
                    const progress = quoteVideos.get(idx)?.videoRenderProgress;
                    return (
                      <div className="mt-3 pt-3 border-t border-border/30" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          {progress != null ? (
                            <>
                              <Progress value={progress} className="h-1.5 flex-1" />
                              <span className="text-xs font-mono text-sky-400 w-10 text-right">{progress}%</span>
                            </>
                          ) : (
                            <>
                              <Progress className="h-1.5 flex-1 animate-pulse" />
                              <span className="text-xs text-sky-400">Rendering…</span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Transcript (quick_catchup / full_recap) */}
      {!isKeyQuotes && quotes.length > 0 && (
        <Card className="bg-card/50 border-border/50" data-testid="card-transcript">
          <CardHeader className="pb-4 border-b border-border/10">
            <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText size={16} className="text-primary" />
                Transcript
                <span className="text-muted-foreground font-normal normal-case tracking-normal text-xs">
                  {quotes.length} segments
                </span>
              </span>
              <div className="flex items-center gap-1">
                {quotes.some((q) => q.words && q.words.length > 0) && (
                  isEditingCuts ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 font-mono text-xs gap-1.5 text-muted-foreground hover:text-foreground"
                        onClick={handleCancelCuts}
                        disabled={isSavingCuts}
                        data-testid="button-cancel-cuts"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        className="h-7 px-2 font-mono text-xs gap-1.5"
                        onClick={handleSaveCuts}
                        disabled={isSavingCuts || editedFlags.size === 0}
                        data-testid="button-save-cuts"
                      >
                        {isSavingCuts ? <Loader2 size={12} className="animate-spin" /> : <Scissors size={12} />}
                        {isSavingCuts ? "Saving" : "Save cuts"}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 font-mono text-xs gap-1.5 text-muted-foreground hover:text-foreground"
                      onClick={() => setIsEditingCuts(true)}
                      data-testid="button-edit-cuts"
                    >
                      <Scissors size={12} />
                      Edit cuts
                    </Button>
                  )
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 font-mono text-xs gap-1.5 text-muted-foreground hover:text-foreground"
                  onClick={() => void copyTranscript(quotes)}
                >
                  {copied ? <Check size={12} className="text-primary" /> : <Copy size={12} />}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 pb-0">
            <div
              className={[
                "relative",
                isFullRecap && !transcriptExpanded
                  ? "max-h-56 overflow-hidden"
                  : "max-h-[480px] overflow-y-auto",
              ].join(" ")}
            >
              <div className="space-y-0.5 pb-4 pr-2">
                {quotes.map((seg, idx) => {
                  const prevSeg = idx > 0 ? quotes[idx - 1] : null;
                  const showSpeaker = !prevSeg || prevSeg.speaker !== seg.speaker;
                  const segWords = seg.words as WordItem[] | undefined;
                  const persistedCuts = seg.wordCuts as WordCutRange[] | undefined;
                  const editedFlagsForSeg = editedFlags.get(seg.segment_id);
                  const isDirty = !!editedFlagsForSeg;
                  const cutFlags = editedFlagsForSeg ?? rangesToFlags(persistedCuts, segWords?.length ?? 0);
                  const effectiveCutRanges = isDirty ? flagsToRanges(cutFlags) : (persistedCuts ?? []);
                  const secondsCut = secondsCutFromRanges(segWords, effectiveCutRanges);
                  return (
                    <div key={seg.segment_id} className={showSpeaker && idx > 0 ? "pt-4" : ""}>
                      {showSpeaker && seg.speaker && (
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <User size={10} className="text-muted-foreground" />
                          <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                            {seg.speaker}
                          </span>
                          <span className="text-xs font-mono text-muted-foreground/40">
                            {formatTime(seg.start / 1000)}
                          </span>
                        </div>
                      )}
                      <p className="text-sm leading-relaxed text-foreground/85 pl-4 border-l border-border/40">
                        <SegmentText
                          text={seg.text}
                          words={segWords}
                          cutFlags={cutFlags}
                          isEditable={isEditingCuts}
                          isDirty={isDirty}
                          onToggleWord={(i) => toggleWordCut(seg.segment_id, i, segWords?.length ?? 0, persistedCuts)}
                        />
                        {secondsCut > 0 && (
                          <span
                            className="ml-2 text-xs font-mono text-muted-foreground/60"
                            title="Seconds removed by intra-sentence cuts"
                            data-testid={`segment-saved-${seg.segment_id}`}
                          >
                            (−{secondsCut.toFixed(1)}s)
                          </span>
                        )}
                      </p>
                    </div>
                  );
                })}
              </div>
              {isFullRecap && !transcriptExpanded && (
                <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-card to-transparent pointer-events-none" />
              )}
            </div>
            <div className={["flex justify-center", isFullRecap ? "pb-3 pt-1" : "pb-2"].join(" ")}>
              {isFullRecap && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="font-mono text-xs gap-1.5 text-muted-foreground hover:text-foreground"
                  onClick={() => setTranscriptExpanded((e) => !e)}
                >
                  {transcriptExpanded ? (
                    <><ChevronUp size={14} /> Collapse</>
                  ) : (
                    <><ChevronDown size={14} /> Show full transcript ({quotes.length} segments)</>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Result Metrics */}
      <Card className="bg-card/50 border-border/50">
        <CardHeader className="pb-4 border-b border-border/10">
          <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
            <FileTerminal size={16} className="text-primary" />
            Result Metrics
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="flex justify-between items-center py-2 border-b border-border/20">
            <span className="text-sm font-mono text-muted-foreground flex items-center gap-2">
              <Clock size={14} /> Original Length
            </span>
            <span className="font-mono" data-testid="text-original-duration">
              {formatTime(job.originalDurationSeconds ?? 0)}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-border/20">
            <span className="text-sm font-mono text-muted-foreground flex items-center gap-2">
              <Scissors size={14} /> Final Length
            </span>
            <span className="font-mono text-primary font-bold" data-testid="text-output-duration">
              {formatTime(job.outputDurationSeconds ?? 0)}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-border/20">
            <span className="text-sm font-mono text-muted-foreground flex items-center gap-2">
              <HardDrive size={14} /> Compression Achieved
            </span>
            <span className="font-mono" data-testid="text-compression">
              {compressionPct !== null ? `${compressionPct}%` : "—"}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-border/20">
            <span className="text-sm font-mono text-muted-foreground flex items-center gap-2">
              <Activity size={14} /> Segments Removed
            </span>
            <span className="font-mono" data-testid="text-segments-removed">
              {job.segmentsOriginal != null && job.segmentsFinal != null
                ? job.segmentsOriginal - job.segmentsFinal
                : "—"}
            </span>
          </div>
          {isKeyQuotes && (
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-mono text-muted-foreground flex items-center gap-2">
                <Quote size={14} /> Key Quotes Extracted
              </span>
              <span className="font-mono text-primary font-bold">{quotes.length}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-4">
        <Button asChild variant="outline" className="font-mono text-xs uppercase tracking-wider">
          <Link href={`/jobs/${jobId}`}>Back to Status</Link>
        </Button>
        <Button asChild variant="ghost" className="font-mono text-xs uppercase tracking-wider">
          <Link href="/">New Job</Link>
        </Button>
      </div>
    </div>
  );
}
